/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ComponentFactoryResolver,
  HostListener, forwardRef, OnDestroy
} from '@angular/core';
import { SearchableItem } from 'src/app/model/interface/searchable.item';
import { ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ReportingService, ModeService } from 'src/app/services';
import { SystemType } from 'src/app/model/enums/system.type';

const noop = (): any => { };

@Component({
  selector: 'multiple-input',
  templateUrl: 'multiple-input-search.component.html',
  styleUrls: ['multiple-input-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultipleInputSearchComponent),
      multi: true
    }
  ],
})
export class MultipleInputSearchComponent implements OnInit, ControlValueAccessor, OnDestroy {
  @ViewChild('rootElement') rootElement: ElementRef;
  @ViewChild('inputText') inputRef: ElementRef;
  @ViewChild('container') containerRef: ElementRef;
  @Input() preset: SearchableItem[] = [];
  @Input() required = false;
  @Input() placeholder = 'Type anything you want';
  @Input() disabled = false;
  @Input() name: string;
  @Input() presetHeader: string = null;
  @Input() arrValue: any[] = [];
  @Input() invalid = false;
  @Input() showValue = false;
  @Input() displayFull = false;
  @Output() valueChange: EventEmitter<SearchableItem[]> = new EventEmitter();

  searchQuery = '';

  private onChange: (_: any) => void = noop;
  private onTouched: () => void = noop;

  isOpen = false;
  selectedIndex: any = null;
  topFlag = false;
  selected = false;
  page = 1;
  totalItems = 0;
  isLoadMore = false;
  isErrorState = false;
  currentMode: SystemType;
  modeSub: any;

  constructor(
    private _scrollToService: ScrollToService,
    private resolver: ComponentFactoryResolver,
    private reportingService: ReportingService,
    private modalService: BsModalService,
    private modeService: ModeService
  ) { }

  get value(): any {
    return this.arrValue;
  }

  set value(val: any) {
    this.arrValue = val;
    this.onChange(val);
  }

  registerOnChange(fn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  writeValue(value: any): void {
    this.arrValue = value;
  }

  ngOnInit(): void {
    this.modeSub = this.modeService.mode$.subscribe(mode => this.currentMode = mode);
  }

  ngOnDestroy(): void {
    if (this.modeSub)
      this.modeSub.unsubscribe();
  }

  getValue(value: any): any {
    if (this.preset) {
      for (const v of this.preset)
        if (v.getValue() === value)
          return this.showValue ? v.getValue() : v.getName();
    }

    if (typeof value.getName === 'function')
      return value.getName();

    return value;
  }

  onSelected(selected: SearchableItem): void {
    if(!this.arrValue)
      this.arrValue = [];

    let found = this.arrValue.find(v => v === selected.getValue());

    if(!found){
      this.arrValue.push(selected.getValue());
    }

    this.valueChange.emit(this.arrValue);

    this.isOpen = false;
    this.selected = true;

    // Propagate ngModel Change
    this.onChange(this.arrValue);
  }

  onShow(): void {
    if (!this.disabled) {
      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        if (this.isInViewport()) {
          this.topFlag = false;
        } else {
          this.topFlag = true;
        }
      }
    }
  }

  @HostListener('document:click', ['$event.target'])
  clickedOutside(targetElement: any): void {
    const clickedInside = this.rootElement.nativeElement.contains(targetElement);
    if (!clickedInside)
      this.isOpen = false;
  }

  isInViewport(): boolean {
    if (this.rootElement.nativeElement) {
      const bounding = this.rootElement.nativeElement.getBoundingClientRect();
      return (
        bounding.top >= 0 &&
        bounding.bottom + 200 <= (window.innerHeight || document.documentElement.clientHeight));
    }

    return false;
  }

  onManage(): void {
    this.reportingService.onManage.emit();
  }

  removeSelected(index: number): void {
    if(!this.disabled){
      this.arrValue.splice(index, 1);
      this.valueChange.emit(this.arrValue);
    }

  }
}
